import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { DataList, DataItem, Image, ImagePlaceholder, PaginationContainer, PageButton } from './Dashboard.styles';
import { Project } from '../types';
import { DetailContainer, Header } from './Detail.styles';
import logo from '../assets/logo.png';

const ProjectsPage: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState<number>(1);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

    const setPage = (newPage: number) => {
        setSearchParams({ page: String(newPage) });
    };

    const fetchData = async (currentPage: number) => {
        setLoading(true);
        try {
            const response = await api.get(`/api/projects?page=${currentPage}&limit=10`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            // Handle both paginated and non-paginated responses
            const projectsArray = response.data.data || response.data;
            setProjects(Array.isArray(projectsArray) ? projectsArray : []);
            setTotalPages(response.data.last_page || 1);
        } catch (error) {
            setError('Error fetching projects');
            console.error('Error fetching projects', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page);
    }, [page]);

    const getDisplayImageUrl = (project: Project): string | null => {
        const firstPlan = project.sections.find(s => s.plan)?.plan;
        if (firstPlan) {
            if (typeof firstPlan === 'string') {
                return `${process.env.REACT_APP_API_URL}/${firstPlan}`;
            }
            if ('url' in firstPlan) {
                return firstPlan.url;
            }
        }

        const firstImage = project.sections.flatMap(s => s.images)[0];
        if (firstImage) {
            if (typeof firstImage === 'string') {
                return `${process.env.REACT_APP_API_URL}/${firstImage}`;
            }
            if ('url' in firstImage) {
                return firstImage.url;
            }
        }

        return null;
    };

    if (loading) {
        return <DetailContainer>Loading...</DetailContainer>;
    }

    if (error) {
        return <DetailContainer>{error}</DetailContainer>;
    }

    return (
        <DetailContainer>
            <Header>
                <div>
                    <img src={logo} alt="Apply Heating Logo" />
                </div>
                <h2>Projects</h2>
            </Header>
            <DataList>
                {projects.map((project) => {
                    const imageUrl = getDisplayImageUrl(project);

                    return (
                        <DataItem key={project.id} onClick={() => navigate(`/detail/${project.id}`)}>
                            <p>Klant: <strong>{project.klant}</strong></p>
                            <p>Adres: <strong>{project.adres}</strong></p>
                            <p>Info: <strong>{project.info}</strong></p>
                            {imageUrl ? <Image src={imageUrl} alt={project.title} /> : <ImagePlaceholder />}
                        </DataItem>
                    );
                })}
            </DataList>
            <PaginationContainer>
                <PageButton onClick={() => setPage(page - 1)} disabled={page === 1}>
                    Previous
                </PageButton>
                <span>Page {page} of {totalPages}</span>
                <PageButton onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                    Next
                </PageButton>
            </PaginationContainer>
        </DetailContainer>
    );
};

export default ProjectsPage;
